import { useEffect, useState, useMemo, useRef } from 'react';
import Select, { components } from 'react-select';
import classNames from 'classnames';
import { ENV_CONFIG } from 'config/environment';
import { getIsRecipient, getIsSuper, getRoleDisplay } from 'config/permissions';
import { Toast } from 'components/Toast';
import Icon, { IconNames } from 'components/Icon';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { getUserData, getUserState } from 'selectors/user';
import {
  getUserName,
  getSelectedClient,
  setSelectedClient,
  getClients,
  getRoleTypeOptions,
} from 'services/storage';
import { setUserClientID } from 'store/user/actions';
import colors from 'constants/colors';
import { resourceSelectStyles } from 'helpers/styles';
import { useNavbarCommon } from '../../hooks/useNavbarCommon';
import { useGetAdminsPartnersQuery } from 'services/adminsApi';
import { buildClientsList, IResourceID } from './clientsList';
import { sameId } from 'helpers/utils';

interface AdminPartnerNavbarProps {
  activeRole: string;
}

const DonationDashNavbar = ({ activeRole }: AdminPartnerNavbarProps) => {
  const {
    isAuthenticated,
    isLoading,
    sidebarOpen,
    toggleSidebar,
    handleLogout,
    isOpen,
    toggle,
    handleChangeRoleType,
  } = useNavbarCommon();

  const dispatch = useAppDispatch();
  const [clients, setClients] = useState<IResourceID[]>([]);
  const [clientIDSelected, setClientIDSelected] = useState<IResourceID>();

  const userState = useAppSelector(getUserState);
  const userData = useAppSelector(getUserData);

  // Fetch partners using RTK Query - don't skip for superadmins
  const { data: partners = [], refetch: refetchPartners } = useGetAdminsPartnersQuery(undefined, {
    skip: !activeRole || getIsRecipient(activeRole) || !isAuthenticated || isLoading,
  });

  // Memoize the clients list calculation
  const finalizedClientsList = useMemo(
    () => buildClientsList(partners, userState.clients, getClients() || [], getIsSuper(activeRole)),
    [partners, activeRole, userState.clients]
  );

  // Create a stable key for the clients list to prevent unnecessary updates
  const clientsListKey = useMemo(
    () => JSON.stringify(finalizedClientsList.map(c => ({ id: c.id, name: c.name }))),
    [finalizedClientsList]
  );
  const clientsListKeyRef = useRef<string>('');
  const finalizedClientsListRef = useRef<IResourceID[]>([]);
  const hasInitializedRef = useRef<boolean>(false);

  // Keep ref in sync with the latest value
  useEffect(() => {
    finalizedClientsListRef.current = finalizedClientsList;
  }, [finalizedClientsList]);

  const selectedClientOption = useMemo(() => {
    if (clientIDSelected?.id == null) return null;
    return clients.find(opt => sameId(opt.id, clientIDSelected.id)) || null;
  }, [clientIDSelected, clients]);

  const handleChangeClient = item => {
    if (!sameId(item.value, clientIDSelected?.id)) {
      trackEvent('Client Switched', { client_id: item.value });
    }
    dispatch(setUserClientID(item.value)); //ReduxState
    setClientIDSelected({ id: item.value, name: item.label }); //UseState
    setSelectedClient({ id: item.value, name: item.label }); //LocalStorage
    // Refetch partners when client changes
    refetchPartners();
  };

  // Update clients list only when the actual data changes
  useEffect(() => {
    if (clientsListKey !== clientsListKeyRef.current) {
      clientsListKeyRef.current = clientsListKey;
      setClients(finalizedClientsListRef.current);
    }
  }, [clientsListKey]);

  // Initialize clientIDSelected from localStorage on mount
  useEffect(() => {
    const storedClient = getSelectedClient();
    if (storedClient) {
      setClientIDSelected(storedClient);
      dispatch(setUserClientID(storedClient.id));
      hasInitializedRef.current = true;
    }
  }, []); // Run only on mount

  // Initialize client selection when clients list is available
  useEffect(() => {
    if (clients.length === 0) return;

    const storedClient = getSelectedClient();
    const isSuper = getIsSuper(activeRole);
    let next: IResourceID | undefined;

    // If we already have a valid selection that exists in the clients list, preserve it
    if (clientIDSelected?.id) {
      const existingClient = clients.find(c => sameId(c.id, clientIDSelected.id));
      if (existingClient) {
        // Current selection is valid, mark as initialized and return
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
        }
        return;
      }
    }

    // No valid selection yet, initialize from localStorage or default
    if (isSuper) {
      // For superadmins, try to restore stored client, otherwise default to "ALL"
      if (storedClient && clients.find(c => sameId(c.id, storedClient.id))) {
        next = { id: storedClient.id, name: storedClient.name };
      } else {
        // Only default to "ALL" if we haven't initialized yet or if stored client doesn't exist
        if (!hasInitializedRef.current || !storedClient) {
          next = { id: 0, name: 'ALL' };
        } else {
          // Stored client exists but not in current list, keep current selection
          return;
        }
      }
    } else {
      // For non-superadmins, try to restore stored client, otherwise default to first client
      if (
        storedClient &&
        storedClient.id !== 0 &&
        clients.find(c => sameId(c.id, storedClient.id))
      ) {
        next = { id: storedClient.id, name: storedClient.name };
      } else {
        // Only default to first client if we haven't initialized yet or if stored client doesn't exist
        if (!hasInitializedRef.current || !storedClient) {
          next = clients[0];
        } else {
          // Stored client exists but not in current list, keep current selection
          return;
        }
      }
    }

    // Only update if we have a valid next value and it's different from current
    if (
      next &&
      (!clientIDSelected ||
        !sameId(clientIDSelected.id, next.id) ||
        clientIDSelected.name !== next.name)
    ) {
      setClientIDSelected(next);
      setSelectedClient(next);
      dispatch(setUserClientID(next.id));
      hasInitializedRef.current = true;
    }
  }, [clients, activeRole]);

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <Icon
          name={props.selectProps.menuIsOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
          className="chevron-svg-oath-blue"
          color={colors.white}
        />
      </components.DropdownIndicator>
    );
  };

  const NoOptionsMessage = (props: any) => {
    return <components.NoOptionsMessage {...props}>No options</components.NoOptionsMessage>;
  };

  return (
    <>
      <nav className="navbar-absolute fixed-top navbar-transparent navbar navbar-expand-lg">
        <Toast />
        <div className="container-fluid">
          <div className="navbar-wrapper">
            <div
              className={classNames('navbar-toggle', {
                toggled: sidebarOpen,
              })}
            >
              <button className="navbar-toggler" onClick={toggleSidebar}>
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </button>
            </div>
            <a href="#" className="navbar-brand" onClick={e => e.preventDefault()}>
              <span className="d-none d-md-block">{userData?.client_name}</span>
            </a>
          </div>
          <div className="navbar-clients-col col-6 col-lg-3 col-md-6 col-sm-6 col-xs-6">
            <div className="navbar-select hidden-on-mobile">
              <Select
                className="navbar-clients"
                classNamePrefix="react-select"
                name="singleSelect"
                value={
                  clientIDSelected
                    ? { value: clientIDSelected.id, label: clientIDSelected.name }
                    : null
                }
                onChange={handleChangeClient}
                options={clients.map(item => ({ value: item.id, label: item.name }))}
                styles={resourceSelectStyles(selectedClientOption)}
                placeholder="Select Client"
                components={{ DropdownIndicator, NoOptionsMessage }}
              />
            </div>
            <img
              src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/oath_logos/Oath_FullLockup_Color_LightText.svg`}
              className="logo-img-file"
              alt="Oath Logo"
            />
          </div>
          <button type="button" className="navbar-logout padding0" onClick={handleLogout}>
            Log Out
          </button>
          <div className="col-md-6 col-sm-6 col-xs-6 padding0">
            <div className="justify-content-end collapse navbar-collapse">
              <Icon
                name={IconNames.User}
                className="oath-user-svg"
                color={colors.white}
                size={20}
              />
              <div className="navbar-user">
                <span className="oath-body inkBlue">{getUserName()}</span>
                <span className="oath-utility inkBlue">{getSelectedClient()?.name}</span>
              </div>
              <ul className="navbar-nav">
                <li
                  className={classNames('btn-rotate dropdown nav-item', {
                    show: isOpen,
                  })}
                >
                  <a
                    aria-haspopup={true}
                    color="default"
                    data-toggle="dropdown"
                    id="navbarDropdownMenuLink"
                    aria-expanded={isOpen}
                    onClick={toggle}
                  >
                    <Icon
                      name={isOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
                      className="chevron-svg-inkblue"
                      color={colors.white}
                    />
                  </a>
                  {getRoleTypeOptions().length > 0 && (
                    <div
                      role="menu"
                      aria-hidden={!isOpen}
                      className={classNames('dropdown-menu dropdown-menu-right', {
                        show: isOpen,
                      })}
                      aria-labelledby="navbarDropdownMenuLink"
                    >
                      {Array.from(getRoleTypeOptions() as string[])
                        .filter(roleType => roleType !== activeRole)
                        .map(roleType => (
                          <button
                            key={roleType}
                            onClick={() => handleChangeRoleType(roleType)}
                            type="button"
                            className="dropdown-item"
                            role="menuitem"
                          >
                            {`Change to ${getRoleDisplay(roleType)} View`}
                          </button>
                        ))}
                      <a href="#" onClick={handleLogout} className="dropdown-item" role="menuitem">
                        Log Out
                      </a>
                    </div>
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="hidden-on-desktop full-width">
          <div className="container-fluid navbar-clients-container">
            <div className="navbar-clients-col col-md-12 is-mobile">
              <div className="row align-center justify-center">
                <Icon
                  name={IconNames.User}
                  className="oath-user-svg"
                  color={colors.white}
                  size={20}
                />
                <div className="navbar-select">
                  <Select
                    className="navbar-clients"
                    classNamePrefix="react-select"
                    name="singleSelect"
                    value={
                      clientIDSelected
                        ? { value: clientIDSelected.id, label: clientIDSelected.name }
                        : null
                    }
                    onChange={handleChangeClient}
                    options={clients.map(item => ({ value: item.id, label: item.name }))}
                    placeholder="Select Client"
                    components={{ DropdownIndicator, NoOptionsMessage }}
                  />
                  <span className="oath-bodysmall white">{getUserName()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default DonationDashNavbar;
